import { readQ7_3Answers } from './q7_3Storage';

/** localStorage for Q7.4 — selected outlet or “can’t find location”. */
export const Q7_4_STORAGE_KEY = 'ploomsurvey:q7_4';

export type Q7_4Answers = {
  /** What we persist as the Q7.4 response: textarea text, or the “can’t find location” label. */
  answer: string;
  cantFindLocation: boolean;
  /** Set when `cantFindLocation` is false and an outlet was chosen from search. */
  outletUuid: string | null;
  /** Outlet address, or free text when customer couldn’t find a match. */
  address: string;
  outletName: string | null;
};

/** Synthetic Q7.4 when Q7.3 is No — user skips the outlet question. */
export const Q7_4_WHEN_Q7_3_NO: Q7_4Answers = {
  answer: 'Not applicable — Q7.3 No (no Out Of Stock experience).',
  cantFindLocation: true,
  outletUuid: null,
  address: '',
  outletName: null,
};

/** Stored Q7.4, or a placeholder when Q7.3 is No (no Q7.4 screen). */
export function getEffectiveQ7_4Answers(): Q7_4Answers | null {
  const stored = readQ7_4Answers();
  if (stored) return stored;
  if (readQ7_3Answers()?.oos === 'No') return Q7_4_WHEN_Q7_3_NO;
  return null;
}

/** True if the survey has a usable Q7.4 answer (including the Q7.3-No shortcut). */
export function isQ7_4CompleteForSurvey(): boolean {
  return getEffectiveQ7_4Answers() != null;
}

export function readQ7_4Answers(): Q7_4Answers | null {
  try {
    const raw = localStorage.getItem(Q7_4_STORAGE_KEY);
    if (!raw) return null;
    const o = JSON.parse(raw) as unknown;
    if (typeof o !== 'object' || o === null) return null;

    const cantFindLocation = (o as { cantFindLocation?: unknown }).cantFindLocation === true;
    const outletUuidRaw = (o as { outletUuid?: unknown }).outletUuid;
    const address = (o as { address?: unknown }).address;
    const outletName = (o as { outletName?: unknown }).outletName;
    const answerRaw = (o as { answer?: unknown }).answer;

    if (cantFindLocation) {
      if (typeof address !== 'string') return null;
      const answer =
        typeof answerRaw === 'string' && answerRaw.trim().length > 0
          ? answerRaw
          : "Sorry, I can't find my location";
      return {
        answer,
        cantFindLocation: true,
        outletUuid: null,
        address,
        outletName: null,
      };
    }

    if (typeof outletUuidRaw !== 'string' || outletUuidRaw.length === 0) return null;
    if (typeof address !== 'string') return null;
    if (outletName != null && typeof outletName !== 'string') return null;
    const derived =
      typeof answerRaw === 'string' && answerRaw.trim().length > 0
        ? answerRaw.trim()
        : [outletName?.trim(), address.trim()].filter(Boolean).join('\n');
    const answer = derived.length > 0 ? derived : address.trim() || '(no text)';
    return {
      answer,
      cantFindLocation: false,
      outletUuid: outletUuidRaw,
      address,
      outletName: outletName ?? null,
    };
  } catch {
    return null;
  }
}

export function writeQ7_4Answers(data: Q7_4Answers): void {
  try {
    localStorage.setItem(Q7_4_STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* ignore */
  }
}

export function clearQ7_4Answers(): void {
  try {
    localStorage.removeItem(Q7_4_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function isQ7_4Complete(): boolean {
  return readQ7_4Answers() != null;
}
